import { landingImages, LandingSvg } from 'assets';

/**
 * ABA Scheduling Software landing page content.
 * Copy transcribed verbatim from the "TherapyLake Search Landing Pages V_4" spec.
 *
 * PENDING (owner): images (`landingImages.abaScheduling*` keys not yet in assets)
 * and section badge SVGs — `BadgeIcon` uses closest existing icons as placeholders.
 */
export const abaSchedulingFeatures = [
    {
        id: "scheduling-guardrails",
        title: "Built-In Scheduling Guardrails",
        text: "Avoid scheduling mistakes that can create billing, authorization, or operational issues later." +
            " TherapyLake includes built-in scheduling guardrails that help your team catch potential problems before they become downstream headaches.",
        bullets: [
            "Help prevent authorization overuse while scheduling",
            "Catch appointment duration issues before they affect billing",
            "Reduce avoidable scheduling mistakes",
            "Support cleaner billing and fewer corrections later",
        ],
        BadgeIcon: LandingSvg.ShieldTickSvg,
        badgeLabel: 'PREVENT ISSUES EARLY',
        imageSmall: landingImages.abaSchedulingGuardrailsImgSmall,
        image: landingImages.abaSchedulingGuardrailsImg,
    },
    {
        id: "recurring-appointments",
        title: "Flexible Recurring Appointment Creation",
        text: "Build ongoing therapy schedules without manually creating every session one by one." +
            "Create appointment series using flexible recurrence options, including selected weekdays, every X days, or monthly patterns such as the 5th day of each month.",
        bullets: [
            "Create recurring appointments for ongoing therapy schedules",
            "Auto select the start and end dates of an appointment series based on authorization dates",
            "Set sessions by weekday, custom day intervals, or monthly rules",
            "Save time when building long-term client schedules",
            "Adjust recurring patterns as scheduling needs change",
        ],
        BadgeIcon: LandingSvg.RepeatSvg,
        badgeLabel: 'BUILD RECURRING CARE',
        imageSmall: landingImages.abaSchedulingRecurringImgSmall,
        image: landingImages.abaSchedulingRecurringImg,
    },
    {
        id: "authorization-context",
        title: "Authorization Details While You Schedule",
        text: "Keep authorization information visible when it matters most: during scheduling." +
            "TherapyLake gives your team quick access to service and authorization details, including remaining hours, so admins can make better scheduling decisions before appointments are created.",
        bullets: [
            "View authorization details while scheduling",
            "See remaining hours before creating appointments",
            "Choose the correct service for each session",
            "Create appointment series based on flexible recurrence options",
        ],
        BadgeIcon: LandingSvg.ReceiptSvg,
        badgeLabel: 'ASSIGN WITH CONFIDENCE',
        imageSmall: landingImages.abaSchedulingAuthImgSmall,
        image: landingImages.abaSchedulingAuthImg,
    },
    {
        id: "staff-recommendations",
        title: "Recommended Staff for Each Appointment",
        text: "TherapyLake helps your team choose the right staff member by recommending eligible staff for appointments.The system considers important scheduling requirements behind the scenes, " +
            "helping admins make faster assignment decisions without manually checking every detail.",
        bullets: [
            "Get staff recommendations while creating appointments",
            "Help ensure staff are appropriate for the selected service",
            "Reduce manual checking during scheduling",
            "Make assignments faster and more confidently",
        ],
        BadgeIcon: LandingSvg.Profile2UserSvg,
        badgeLabel: 'SEE OPEN TIME',
        imageSmall: landingImages.abaSchedulingStaffImgSmall,
        image: landingImages.abaSchedulingStaffImg,
    },

    {
        id: "availability-calendar",
        title: "See Client and Staff Availability in One Calendar",
        text: "Make scheduling easier by viewing client availability, staff availability, and overlapping time windows directly on the calendar. Instead of switching between notes, spreadsheets, or separate availability records, TherapyLake " +
            "highlights availability on the schedule so your team can quickly see where appointments can actually" +
            " fit.",
        bullets: [
            "View client and staff availability directly on the calendar",
            "Identify overlapping availability with clear visual highlights",
            "Reduce back-and-forth when finding the right appointment time",
            "Schedule faster with a clearer view of open time windows",
        ],
        BadgeIcon: LandingSvg.Calendar2Svg,
        badgeLabel: 'IMPROVE PLANNING',
        imageSmall: landingImages.abaSchedulingAvailabilityImgSmall,
        image: landingImages.abaSchedulingAvailabilityImg,
    },

    {
        id: "scheduling-analytics",
        title: "Scheduling Analytics and Exports",
        text: "Turn your schedule into actionable operational insight. " +
            "TherapyLake helps teams monitor authorization utilization, staff readiness, eligibility," +
            " and scheduling performance so admins can make better planning decisions.",
        bullets: [
            "Track authorization utilization and identify underused hours",
            "Review staff compliance before scheduling",
            "Run eligibility checks to support cleaner scheduling decisions",
            "Export scheduling data for reporting and operations",
        ],
        BadgeIcon: LandingSvg.ChartSvg,
        badgeLabel: 'SCHEDULE WITH INSIGHT',
        imageSmall: landingImages.abaSchedulingAnalyticsImgSmall,
        image: landingImages.abaSchedulingAnalyticsImg,
    },
];

export const abaSchedulingFaqs = [
    {
        title: "Does TherapyLake support ABA appointment scheduling?",
        description: "Yes. TherapyLake helps ABA teams manage appointments, recurring sessions, staff assignments," +
            " client availability, authorization details, and scheduling-related reporting.",
    },
    {
        title: "Can TherapyLake help reduce scheduling mistakes?",
        description: "Yes. TherapyLake includes scheduling guardrails that help teams catch authorization, duration," +
            " and appointment setup issues before they create billing or operational problems.",
    },
    {
        title: "Does TherapyLake support recurring ABA appointments?",
        description: "Yes. TherapyLake supports flexible recurring appointment creation, including selected weekdays," +
            " custom day intervals, and monthly scheduling rules.",
    },
];
